import moment from 'moment-timezone';

import { fromSlug } from '@techchange/components';
import { messages } from '../i18n';

import { RESOURCE_TYPES } from '../constants';

/**
 * Calculates the width of an item based on its sibling elements.
 *
 * @param   {array} siblings  The sibling elements that come before the element.
 * @returns {number}          The calculated weight of the element.
 */
export function calculateResourceItemWidth(siblings) {

	let rowTotal = 0;

	if (siblings) {

		// Loop through each of the sibling elements
		for (let i = 0; i < siblings.length; i++) {

			// Check if the next sibling wraps onto the next row
			if (rowTotal + siblings[i].width > 12) {

				// Reset the total widths, b/c we're onto the next row
				rowTotal = 0;

			}

			rowTotal += siblings[i].width;

		}

	}

	// The default value for the new width is full-width, i.e. 12
	let result = 12;

	// Check that the new width will be a valid value
	if (rowTotal === 3 ||
		rowTotal === 4 ||
		rowTotal === 6 ||
		rowTotal === 8 ||
		rowTotal === 9) {

		result = 12 - rowTotal;

	}

	return result;

}

/**
 * Calculates the total width of a row of resources.
 *
 * @param   {array} row  The row of resources.
 * @returns {number}     The width of the row.
 */
export function calculateResourceRowWidth(row) {

	return row.reduce((acc, item) => acc + item.width, 0);

}

/**
 * Determines if a resource is active given the location pathname.
 *
 * @param   {string} pathname  The url path.
 * @param   {object} resource  The resource.
 * @returns {boolean}          True, if the resource is active.
 */
export function isResourceActive(pathname, resource) {

	if (pathname && resource && !isResourceContent(resource)) {

		const path = pathname.split('/');

		let match;

		try {

			// Check for match of resource id based on resource UUID encoded in the url
			match = path.find((slug) => fromSlug(slug) === resource.id);

		} catch (error) {
			console.error(error);
		}

		// Check for match of resource id based on 8 char substring of id (deprecated)
		const idSubstring = resource.id.substring(0, 8);

		const deprecatedMatch1 = path.find((slug) => slug.indexOf(idSubstring) >= 0);

		// Check for exact match of resource slug (deprecated)
		const deprecatedMatch2 = path.find((slug) => slug === resource.slug);

		if (match || deprecatedMatch1 || deprecatedMatch2) {
			return true;
		}

	}

	return false;

}

/**
 * Determines if the resource is a piece of content
 * at the bottom level of the resource tree.
 *
 * @param {object} resource  The resource.
 *
 * @returns {boolean} result  True, if the resource is content.
 */
export function isResourceContent(resource) {

	switch (resource.type) {
	case (RESOURCE_TYPES.EMBED):
	case (RESOURCE_TYPES.IMAGE):
	case (RESOURCE_TYPES.MIRO):
	case (RESOURCE_TYPES.FORM):
	case (RESOURCE_TYPES.POLL):
	case (RESOURCE_TYPES.QUIZ):
	case (RESOURCE_TYPES.SLIDE):
	case (RESOURCE_TYPES.VIDEO):
		return true;
	default:
		return false;
	}

}

/**
 * Determines if the resource is open based on its open_date.
 *
 * @param {object} resource  The resource.
 *
 * @returns {boolean} result  True, if the resource is open.
 */
export function isResourceOpen(resource) {

	// Resources are open by default
	if (!resource || !resource.open_date) {
		return true;
	}

	// Get the current time
	const now = moment();

	// Get the exact time that the resource opens
	const open = moment(resource.open_date);

	return now.isAfter(open);

}

/**
 * Format the display message indicating when the resource will open.
 *
 * @param {object}  resource      The resource.
 * @param {string}  userTimezone  The current user's timezone.
 * @param {object}  intl          The provider for internationalization.
 * @param {object}  options       Additional options.
 * @param {boolean} options.aria  Flag indicating if a more specific aria label should be returned.
 *
 * @returns {string} result  The formatted display message.
 */
export function formatResourceOpenMessage(resource, userTimezone, intl, options = {}) {

	if (isResourceOpen(resource) || !userTimezone) {
		return null;
	}

	const openDate = moment(resource.open_date).tz(userTimezone);

	const showMinute = openDate.format('mm') !== '00';

	const timeFormat = showMinute ? 'h:mm' : 'h';

	if (options.aria) {
		return intl.formatMessage(messages.TC_COMPONENTS_UTILS_RESOURCE_FORMATTED_OPEN_DATE_TEXT_ARIA_LABEL, {
			title: resource.title,
			time: openDate.format(`${timeFormat}a zz`),
			date: openDate.format('MMMM D, YYYY'),
		});
	}

	return intl.formatMessage(messages.TC_COMPONENTS_UTILS_RESOURCE_FORMATTED_OPEN_DATE_TEXT, {
		time: openDate.format(`${timeFormat}a zz`),
		date: openDate.format('MMMM D, YYYY'),
	});

}

/**
 * Autofills the final url starting from a given resource and the initial url.
 *
 * @param   {object} node      The current resource.
 * @param   {string} pathname  The initial URL.
 *
 * @returns {string} result    The autofilled URL.
 */
export function autofillResourceUrl(node, pathname) {

	// Check if the current resource should be included in the final url...
	if (node.type !== RESOURCE_TYPES.PAGE &&
		node.type !== RESOURCE_TYPES.MODULE &&
		node.type !== RESOURCE_TYPES.SUBMODULE) {
		return '';
	}

	// Choose the active child (if any) for the current resource
	const activeChild = chooseActiveChildResource(node, pathname);

	// If one exists, add it to the final url...
	if (activeChild) {
		return `/${node.slug}${autofillResourceUrl(activeChild, pathname)}`;
	}

	// Otherwise, return the current url...
	return `/${node.slug}`;

}

/**
 * Determines if a module has any nested submodules
 *
 * @param {object} module   A module
 * @returns {boolean}       Whether the module has any nested submodules
 */
function hasNestedSubModules(module) {

	if (module.children) {

		for (let i = 0; i < module.children.length; i++) {

			if (module.children[i].type === RESOURCE_TYPES.SUBMODULE) {
				return true;
			}

		}

	}

	return false;

}

/**
 * Determines if a module is navigable.
 *
 * @param {object} module    The module.
 *
 * @returns {object} result  True, if the module is navigable.
 */
function validNextModule(module) {
	return module.type === RESOURCE_TYPES.SUBMODULE && !hasNestedSubModules(module);
}

/**
 * Returns the previous/next navigable resource in relation to the current resource.
 *
 * @param {object} rootResource     The root resource, with children nested in tree structure.
 * @param {object} currentResource  The current resource.
 *
 * @returns {array} result
 * @returns {object} result[0]  The previous resource (if any).
 * @returns {object} result[1]  The next resource (if any).
 */
export function getAdjacentResources(rootResource, currentResource) {

	let prevResource = null;

	let nextResource = null;

	let selectNext = false;

	if (rootResource) {

		const modules = rootResource.children || [];

		// Loop through each of the modules...
		for (let i = 0, len = modules.length; i < len; i++) {

			const module = modules[i];

			const submodules = module.children || [];

			// Loop through each of the submodules...
			for (let j = 0, jLen = submodules.length; j < jLen; j++) {

				const submodule = submodules[j];

				// Check if the submodule is navigable in relation to the current resource
				if (validNextModule(submodule)) {

					// Check if we're looking for the next resource
					if (selectNext) {

						nextResource = submodule;
						break;

					// Otherwise, check if we've just reached the current resource
					} else if (submodule.id && submodule.id === currentResource.id) {

						selectNext = true;

					// Otherwise, we've just found the previous resource
					} else {

						prevResource = submodule;

					}

				// Otherwise check if module grandchild is navigable in relation to the current resource
				} else if (submodule.children) {

					const submoduleChildren = submodule.children || [];

					// Loop through each of the submodule's children (i.e. content or subsubmodules)...
					for (let k = 0, kLen = submoduleChildren.length; k < kLen; k++) {

						const submoduleChild = submoduleChildren[k];

						// Check if the submodule child is navigable in relation to the current resource
						if (validNextModule(submoduleChild)) {

							// Check if we're looking for the next resource
							// eslint-disable-next-line max-depth
							if (selectNext) {

								nextResource = submoduleChild;
								break;

							// Otherwise, check if we've just reached the current resource
							} else if (submoduleChild.id && submoduleChild.id === currentResource.id) {

								selectNext = true;

							// Otherwise, we've just found the previous resource
							} else {

								prevResource = submoduleChild;

							}

						}

					}

				// Otherwise the next module should be the next resource if there
				// are no submodules or nested submodules in the current module
				} else if (selectNext) {

					nextResource = module;
					break;

				} else if (module.id === currentResource.id) {

					selectNext = true;
					break;

				} else {

					prevResource = module;

				}

				// Once we find both the previous and next resource, we're good to go
				if (nextResource) {
					break;
				}

			}

			// Once we find both the previous and next resource, we're good to go
			if (nextResource) {
				break;
			}

		}

	}

	return [prevResource, nextResource];

}

/**
 * Determines the active child for a resource.
 *
 * @param {object} resource    The resource.
 * @param {string} pathname    The url path.
 *
 * @returns {object} result    The active child for the resource.
 */
export function chooseActiveChildResource(resource, pathname) {

	// Check to make sure that there are children
	if (!resource.children) {
		return null;
	}

	// Try to choose the child from the url pathname...
	let result = resource.children.find((child) => isResourceActive(pathname, child));

	// Try to choose the child from the parent's default_child_id field...
	if (!result) {
		result = resource.children.find((child) => child.id === resource.default_child_id);
	}

	// Try to choose the first open child...
	if (!result) {

		for (let i = 0, len = resource.children.length; i < len; i++) {

			if (isResourceOpen(resource.children[i])) {

				result = resource.children[i];
				break;

			}

		}

		// If none are open, just use the first child
		if (!result) {
			result = resource.children[0];
		}

	}

	return result;

}

/**
 * Maps resource type to corresponding internationalized message.
 *
 * @param {Object} intl  The provider for internationalization.
 * @param {String} type  The resource type.
 *
 * @returns {Object} Mapping of actions to corresponding metadata.
 */
export const formatIntlResourceType = (intl, type) => {

	const map = {
		[RESOURCE_TYPES.SESSION]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_SESSION,
		[RESOURCE_TYPES.PAGE]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_PAGE,
		[RESOURCE_TYPES.MODULE]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_MODULE,
		[RESOURCE_TYPES.SUBMODULE]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_SUBMODULE,
		[RESOURCE_TYPES.SUBMODULE_CONTENT]:
			messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_SUBMODULE_CONTENT,
		[RESOURCE_TYPES.EMBED]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_EMBED,
		[RESOURCE_TYPES.FORM]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_FORM,
		[RESOURCE_TYPES.IMAGE]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_IMAGE,
		[RESOURCE_TYPES.LINK]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_LINK,
		[RESOURCE_TYPES.MIRO]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_MIRO,
		[RESOURCE_TYPES.POLL]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_POLL,
		[RESOURCE_TYPES.QUIZ]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_QUIZ,
		[RESOURCE_TYPES.SLIDE]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_SLIDE,
		[RESOURCE_TYPES.VIDEO]: messages.TC_COMPONENTS_GENERAL_TEXT_LRS_OBJECT_TYPE_VIDEO,
	};

	return intl.formatMessage(map[type]);

};
